import * as XLSX from "xlsx";
import { esc, DOC_STYLES, docHeader, signatureBlock, makeDocNo, elementToPdf } from "./pdfCommon";

function fmtPeriod(t, from, to) {
  return [from, to].filter(Boolean).join(" — ");
}

export async function downloadCariStatementPdf({ t, fmt, data, userName }) {
  const docNo = makeDocNo("CRS");
  const c = data.cari;
  const tt = data.totals;
  const period = fmtPeriod(t, data.period.from, data.period.to);

  const txRows = data.transactions.map((r) => `<tr>
    <td style="${DOC_STYLES.td};font-family:'JetBrains Mono',monospace;font-size:10.5px;white-space:nowrap">${esc(r.date)}</td>
    <td style="${DOC_STYLES.td}"><span style="color:${r.type === "income" ? "#059669" : "#DC2626"};font-weight:600">${r.type === "income" ? esc(t.app.income) : esc(t.app.expense)}</span></td>
    <td style="${DOC_STYLES.td}">${esc(r.description || r.category)}</td>
    <td style="${DOC_STYLES.tdNum};color:${r.type === "income" ? "#059669" : "#DC2626"}">${r.type === "income" ? "+" : "−"}${fmt(r.amount)}</td>
  </tr>`).join("");

  const pendTable = (title, items, kindLabel) => items.length ? `
    <div style="margin-top:22px;font-size:12.5px;font-weight:600">${esc(title)}</div>
    <table style="width:100%;border-collapse:collapse;margin-top:8px">
      <tr><th style="${DOC_STYLES.th}">${esc(t.app.title)}</th><th style="${DOC_STYLES.th}">${esc(t.app.dueDate)}</th><th style="${DOC_STYLES.thNum}">${esc(t.app.amount)}</th></tr>
      ${items.map((p) => `<tr><td style="${DOC_STYLES.td}">${esc(p.title)}</td><td style="${DOC_STYLES.td};font-family:'JetBrains Mono',monospace;font-size:10.5px">${esc(p.due_date)}</td><td style="${DOC_STYLES.tdNum}">${fmt(p.amount)}</td></tr>`).join("")}
    </table>` : "";

  const box = (label, value, color) =>
    `<div style="flex:1;border:1px solid #E2E8F0;border-radius:10px;padding:12px 16px">
      <div style="font-size:9px;color:#94A3B8;text-transform:uppercase;letter-spacing:1px">${label}</div>
      <div style="margin-top:4px;font-size:16px;font-weight:700;color:${color};font-family:'JetBrains Mono',monospace">${value}</div>
    </div>`;

  const html = `
    ${docHeader(`${t.cariP.statementTitle} — ${c.name}`, period)}
    <div style="margin-top:10px;font-size:11px;color:#64748B">
      ${c.type === "customer" ? esc(t.app.customer) : esc(t.app.supplier)}
      ${c.email ? ` · ${esc(c.email)}` : ""}${c.phone ? ` · ${esc(c.phone)}` : ""}${c.tax_number ? ` · VN: ${esc(c.tax_number)}` : ""}
    </div>
    <div style="margin-top:18px;display:flex;gap:10px">
      ${box(t.rep.totalIncome, fmt(tt.income), "#059669")}
      ${box(t.rep.totalExpense, fmt(tt.expense), "#DC2626")}
      ${box(t.doc.balance, fmt(tt.balance), tt.balance >= 0 ? "#0055FF" : "#D97706")}
    </div>
    <div style="margin-top:22px;font-size:12.5px;font-weight:600">${esc(t.doc.title)}</div>
    <table style="width:100%;border-collapse:collapse;margin-top:8px">
      <tr><th style="${DOC_STYLES.th}">${esc(t.app.date)}</th><th style="${DOC_STYLES.th}">${esc(t.app.income)}/${esc(t.app.expense)}</th><th style="${DOC_STYLES.th}">${esc(t.app.description)}</th><th style="${DOC_STYLES.thNum}">${esc(t.app.amount)}</th></tr>
      ${txRows || `<tr><td colspan="4" style="${DOC_STYLES.td};text-align:center;color:#94A3B8">${esc(t.app.noData)}</td></tr>`}
    </table>
    ${pendTable(t.doc.pendingRec, data.pending_receivables)}
    ${pendTable(t.doc.pendingPay, data.pending_payments)}
    ${signatureBlock({ docNo, userName, t })}
  `;
  await elementToPdf(html, `ira-finans-ekstre-${c.name.replace(/[^a-zA-Z0-9ğüşöçıİĞÜŞÖÇ]/g, "-")}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function downloadCariStatementXlsx({ t, data }) {
  const c = data.cari;
  const tt = data.totals;
  const wb = XLSX.utils.book_new();

  const main = [
    [`${t.cariP.statementTitle} — ${c.name}`],
    [fmtPeriod(t, data.period.from, data.period.to)],
    [],
    [t.rep.totalIncome, tt.income],
    [t.rep.totalExpense, tt.expense],
    [t.doc.balance, tt.balance],
    [],
    [t.app.date, `${t.app.income}/${t.app.expense}`, t.app.category, t.app.description, t.app.bank, t.app.amount],
    ...data.transactions.map((r) => [
      r.date, r.type === "income" ? t.app.income : t.app.expense, r.category, r.description || "", r.bank_account_name || "", r.amount,
    ]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(main), t.app.transactions.slice(0, 31));

  if (data.pending_receivables.length || data.pending_payments.length) {
    const pend = [
      [t.doc.pendingRec],
      [t.app.title, t.app.dueDate, t.app.amount],
      ...data.pending_receivables.map((p) => [p.title, p.due_date, p.amount]),
      [],
      [t.doc.pendingPay],
      [t.app.title, t.app.dueDate, t.app.amount],
      ...data.pending_payments.map((p) => [p.title, p.due_date, p.amount]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pend), t.app.pending.slice(0, 31));
  }

  XLSX.writeFile(wb, `ira-finans-ekstre-${new Date().toISOString().slice(0, 10)}.xlsx`);
}
